import { isPercentageValue, mm2px } from '@/utils/common';
import {
  AnnotationShape,
  ChangeColorCommand,
  RemoveColorCommand,
  RemoveColorResult,
} from '@/web-components/import-wizard/typings';
import _findIndex from 'lodash.findindex';
import { Collider2d } from 'collider2d';
import { defineStore } from 'pinia';
import { useStore as useProductStore } from './product';
import { useStore as useActionsStore } from './action';
import { useStore as useColorMngStore } from './colorsManagement';
import { useStore as useShapeBoxSectionStore } from './shapeBoxSection';
import { useStore as useNotificationStore } from './notification';
import { useStore as useHistoryStore } from './history';
import { useStore as useMirrorStore } from './mirror';
import { useStore as useColorsManagementStore } from '@/stores/colorsManagement';
import { checkIsInArea } from '@/utils/collisionHelper';
import FileService from '@/services/FileService';
import {
  LogoShape,
  TextShape,
  VariantArtworkImages,
  Text,
} from '@/web-components/import-wizard/typings/pintura';
import { base64StringToBlob } from 'blob-util';
import { constructArtworkValidationPipeline } from '../validators/index';
import { ProductPrintPositionBoxModel, SmallDetailsPayload } from '@/typings';
import { PipelineNotificationType } from '@/components/Notifiers/typings/PipelineNotificationType';
import {
  CreatePersonalizationForm,
  PersonalizationText,
  PmsColor,
  ProductVariant,
} from '@midocean/webapps-shared-domain';
import { Personalization } from '@midocean/webapps-shared-domain';
import axios from 'axios';
import {
  SetArtworkBackgroundCommand,
  DoRoundCroppingCommand,
} from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import { ProductPrintPanel } from '@/typings/ProductPrintData';
import { lineWidth } from '@/constants/areaLineWidth';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { getSmallDetailsValidatorColor } from '@/components/SmallDetails/utils/helper';
import { ShapeSnapshot } from '@/typings/history';
import { createStateSnapshot } from '@/utils/historyHelper';
import {
  ProductPrintVariant,
  LaserConfiguration,
} from '@midocean/webapps-shared-domain';
import {
  DefaultSmallDetailsResult,
  ProcessTextCommand,
  TextAlignment,
} from '@/typings/SmallDetails';
import { positionText } from '@/utils/alignText';
import { constructLogoOnlyNewPersonalizationForm } from '@/construct-methods';
import { CreatePersonalizationFormWrapper } from '@/web-components/import-wizard/typings/common/createPersonalization';

const ADD_ARTWORK_PAYLOAD = 'addArtworkPayload';
const REMOVE_ARTWORK_PAYLOAD = 'removeArtworkPayload';
const ADD_TEXT_PAYLOAD = 'addTextPayload';
const REMOVE_TEXT_PAYLOAD = 'removeTextPayload';

export const logoPositionMapper =
  (position: ProductPrintPositionBoxModel, map: Record<string, unknown>[]) =>
    (item: Pick<PipelineNotificationType, 'payloadId'>): boolean => {
      return map.some((m) => m.logoId === item.payloadId && m.id === position.id);
    };

export const nonTextShapes = (shape: AnnotationShape): boolean =>
  shape.kind === 'Logo';

export const textShapes = (shape: AnnotationShape): boolean =>
  shape.kind === 'Text';

export const useStore = defineStore({
  id: 'shape',

  state: () => {
    return {
      activeShapeId: '',
      errors: [],
      collider2d: new Collider2d(),
      shapes: [] as AnnotationShape[],
      positionShapeMap: [] as Record<string, string>[],
      isLoaderActive: false,
      panelPosition: { x: 0, width: 0 } as ProductPrintPanel,
      personalization: {} as Personalization,
      isMoccaPersonalization: false,
    };
  },

  getters: {
    collisionDetails: (
      state
    ): { aInB: boolean; payloadId: string }[] | undefined => {
      const productStore = useProductStore();
      const scaleRatio =
        (productStore.activePosition.originalImageWebDpi as number) /
        (productStore.activePosition.originalImageDpi as number);
      const productDimensions = {
        x:
          (productStore.activePosition.originalImageWidthPx as number) *
          scaleRatio || 227,
        y:
          (productStore.activePosition.originalImageHeightPx as number) *
          scaleRatio || 402,
      };
      const printableAreaShapeType =
        productStore.activePosition.printArea?.shapeType || '';

      const printableArea = productStore.drawPoints();
      const printableAreaRotation = productStore.rotation;

      if (!printableArea) return;

      const collisions = state.shapes
        .filter((shape) =>
          state.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .map((currentShape) => {
          const shapeArea: AnnotationShape = {
            ...currentShape,
            x: isPercentageValue(currentShape.x)
              ? (Number.parseFloat(currentShape.x as unknown as string) / 100) *
              productDimensions.x
              : currentShape.x,
            y: isPercentageValue(currentShape.y)
              ? (Number.parseFloat(currentShape.y as unknown as string) / 100) *
              productDimensions.y
              : currentShape.y,
            width: isPercentageValue(currentShape.width)
              ? (Number.parseFloat(currentShape.width as unknown as string) /
                100) *
              productDimensions.x
              : currentShape.width,
            height: isPercentageValue(currentShape.height)
              ? (Number.parseFloat(currentShape.height as unknown as string) /
                100) *
              productDimensions.y
              : currentShape.height,
            rotation: currentShape.rotation || 0,
            isRoundLogo: currentShape.isRoundLogo,
          };

          const {
            originalImageWebDpi,
            originalImageWidthPx,
            originalImageDpi,
          } = productStore.activePosition;

          const webImageWidthPx =
            originalImageWidthPx &&
            originalImageWebDpi &&
            originalImageDpi &&
            (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
          const rectLineWidth = (lineWidth * (webImageWidthPx as number)) / 100;

          const isInArea = checkIsInArea(
            shapeArea,
            printableArea,
            printableAreaShapeType,
            printableAreaRotation,
            rectLineWidth
          );

          currentShape.isInArea = isInArea;
          const notificationStore = useNotificationStore();

          const notificationIndex =
            notificationStore.notificationsForBell.findIndex(
              (n) =>
                n.payloadId === currentShape.id &&
                n.validatorName === 'outOfBoundaries'
            );

          if (notificationIndex === -1 && !isInArea) {
            notificationStore.notificationsForBell.push({
              validatorColor: 'Red',
              validatorName: 'outOfBoundaries',
              payloadId: currentShape.id,
              fileName:
                currentShape.kind === 'Logo'
                  ? currentShape.title
                  : currentShape.w2pText.text,
              printPositionId: productStore.selectedPrintPosition.id,
              printPosition: productStore.selectedPrintPosition.name,
              message: '',
            });

            notificationStore.allValidatorPipelineResults.push({
              payload: currentShape,
              validationResult: [
                {
                  validatorColor: 'Red',
                  validatorName: 'outOfBoundaries',
                  printPositionId: productStore.selectedPrintPosition.id,
                },
              ],
            });
          }

          if (notificationIndex !== -1 && isInArea) {
            const allValidatorPipelineIndex =
              notificationStore.allValidatorPipelineResults.findIndex(
                (n) =>
                  n.payload.id === currentShape.id &&
                  n.validationResult.validatorName === 'outOfBoundaries'
              );
            if (allValidatorPipelineIndex === -1) {
              notificationStore.allValidatorPipelineResults.splice(
                allValidatorPipelineIndex,
                1
              );
            }

            notificationStore.notificationsForBell.splice(notificationIndex, 1);
          }
          return { aInB: isInArea, payloadId: currentShape.id };
        });

      return collisions;
    },
  },
  actions: {
    /**
     * When editing a position, we also have to reassign existing logo/text shape
     * refs so that they can continue to live on a newly switched position
     */

    resetShapeStateToDefault() {
      this.$patch((state) => {
        state.activeShapeId = '';
        state.errors = [];
        state.collider2d = new Collider2d();
        state.shapes = [];
        state.positionShapeMap = [];
        state.isLoaderActive = false;
        state.panelPosition = { x: 0, width: 0 };
        state.personalization = {} as Personalization;
      });
    },

    setPanelPosition(position: ProductPrintPanel) {
      this.panelPosition = position;
    },
    setIsMoccaPersonalization(isMoccaPersonalization: boolean) {
      this.isMoccaPersonalization = isMoccaPersonalization;
    },
    reassignRefs({ from, to }: { from: string; to: string }) {
      if (from === to) {
        return;
      }
      const reassignedFromTo = this.positionShapeMap.map((psm) => {
        return psm.id === from ? { ...psm, id: to } : psm;
      });

      this.positionShapeMap = reassignedFromTo;
    },

    updateShapeStateFromSnapshot(snapshot: ShapeSnapshot) {
      this.$patch((state) => {
        if (snapshot !== undefined) {
          state.activeShapeId = snapshot.activeShapeId;
          this.shapes.splice(0, this.shapes.length);
          snapshot.shapes.forEach((x) => {
            this.shapes.push(x);
          });
          state.positionShapeMap = snapshot.positionShapeMap;
        }
      });
    },
    async updateColorsAfterImageIsAdjusted(
      shapeId: string,
      doTrimming: boolean
    ) {
      const idx = this.shapes.findIndex((p) => p.id === shapeId);
      const shape = this.shapes[idx];
      if (shape.kind === 'Logo') {
        const { data } = await FileService.detectColors(
          shape.pngFileWithoutConverters,
          doTrimming,
          // Check if the original file is a vector file
          shape.originalHorizontalResolutionDPIs === 0
        );

        const colorManagementStore = useColorsManagementStore();
        colorManagementStore.changeColorsToClosestPantonesFromPalette(
          data.colors
        );

        shape.pmsColorsInPngFile = shape.pmsColorsInPngFile.filter((x) =>
          data.colors.some((color) => color.name === x.name)
        );

        const widthScaleFactor = shape.pngWidthPixels / data.widthInPx;
        const heightScaleFactor = shape.pngHeightPixels / data.heightInPx;
        const newWidth = shape.width / widthScaleFactor;
        const newHeight = shape.height / heightScaleFactor;

        shape.width = newWidth;
        shape.height = newHeight;
        shape.pngWidthPixels = data.widthInPx;
        shape.pngHeightPixels = data.heightInPx;
        shape.aspectRatio = newWidth / newHeight;
        shape.backgroundImage = URL.createObjectURL(
          base64StringToBlob(data.trimmedImageBase64, 'image/png')
        );
        shape.pngFile = base64StringToBlob(
          data.trimmedImageBase64,
          'image/png'
        );
        shape.pngFileWithoutConverters = base64StringToBlob(
          data.trimmedImageBase64,
          'image/png'
        );
      }

      const notificationStore = useNotificationStore();
      notificationStore.revalidateUnavailablePrintPositions();
      notificationStore.initializeSmallDetailsNotifications();
    },

    reinitializeShapeVariantImage(
      variant: ProductPrintVariant,
      textColor: number[]
    ) {
      const productStore = useProductStore();

      const printPositionShapes = this.shapes.filter((shape) =>
        this.positionShapeMap.some(
          (psm) =>
            psm.id === productStore.selectedPrintPosition.id &&
            psm.logoId === shape.id
        )
      );

      printPositionShapes.forEach((s) => {
        const variantArtworkImage = s.variantArtworkImages?.find(
          (i) => i.variantColor === variant.colour
        );

        s.backgroundImage = URL.createObjectURL(
          variantArtworkImage?.variantArtworkPng || (s.pngFile as Blob)
        );

        if (s.kind === 'Text') {
          s.w2pText = {
            ...s.w2pText,
            color: textColor,
          };
        }
      });
    },

    deselectShape() {
      const shapeBoxSectionStore = useShapeBoxSectionStore();
      shapeBoxSectionStore.resetIsValidInput();
      const current = this.shapes.find((s) => s.id === this.activeShapeId);
      if (current !== undefined) {
        current.isSelected = false;
      }
      this.activeShapeId = '';
    },
    selectShape(shapeId: string) {
      const shapeBoxSectionStore = useShapeBoxSectionStore();
      shapeBoxSectionStore.resetIsValidInput();
      this.activeShapeId = shapeId;
    },
    async addArtworkPayload(
      shapePayload: AnnotationShape,
      positionId: string,
      techniqueId: string,
      createSnapshot: boolean
    ) {
      this.shapes.push(shapePayload);
      this.selectShape(shapePayload.id);
    },
    async removeArtworkPayload(
      personalizationArtworkId: string,
      createSnapshot: boolean
    ) {
      this.deselectShape();
    },
    removeShape(id: string) {
      this.deselectShape();

      this.shapes = this.shapes.filter((s) => {
        return s.id !== id;
      });

      this.positionShapeMap = this.positionShapeMap.filter((psm) => {
        return psm.logoId !== id;
      });
    },
    async addTextPayload(shapePayload: AnnotationShape, positionId: string) {
      this.shapes.push(shapePayload);
      this.selectShape(shapePayload.id);
    },
    removeTextPayload(id: string, createSnapshot: boolean) {
      const notificationStore = useNotificationStore();
      const historyStore = useHistoryStore();
      const filteredShapes = this.shapes.filter((s) => {
        return s.id !== id;
      });
      this.shapes.splice(0, this.shapes.length);
      filteredShapes.forEach((x) => {
        this.shapes.push(x);
      });

      this.positionShapeMap = this.positionShapeMap.filter((psm) => {
        return psm.logoId !== id;
      });

      // would be nice to revalidate all instead of just deleting
      notificationStore.allValidatorPipelineResults =
        notificationStore.allValidatorPipelineResults.filter((pr) => {
          return pr.payload.id !== id;
        });

      notificationStore.notificationsForBell =
        notificationStore.notificationsForBell.filter((n) => {
          return n.payloadId !== id;
        });

      this.deselectShape();

      notificationStore.revalidateEmptyPrintPositions();

      if (createSnapshot) {
        const snapshot = createStateSnapshot();
        historyStore.recordHistory(snapshot);
      }
    },
    updateArtworkBackground(
      pngFile: string,
      artworkColors: PmsColor[],
      height: number,
      width: number
    ) {
      const index = this.shapes.findIndex(
        (shape: AnnotationShape) => shape.id === this.activeShapeId
      );

      const affectedShape = this.shapes[index];

      const aspectRatioWH =
        (affectedShape.width as number) / (affectedShape.height as number);
      const aspectRatioHW =
        (affectedShape.height as number) / (affectedShape.width as number);

      let recalculatedWidth = 0;
      let recalculatedHeight = 0;

      if (aspectRatioWH >= aspectRatioHW) {
        recalculatedHeight = (affectedShape.width as number) / (width / height);
        recalculatedWidth = affectedShape.width as number;
      } else {
        recalculatedHeight = affectedShape.height as number;
        recalculatedWidth = (affectedShape.height as number) / (height / width);
      }

      const smallDetailsResult = affectedShape.smallDetailsResult
        ? { ...affectedShape.smallDetailsResult, isCheckValid: false }
        : undefined;

      this.$patch((state) => {
        const current = state.shapes[index];
        if (current.kind === 'Logo') {
          state.shapes.splice(index, 1, {
            ...current,
            backgroundImage: URL.createObjectURL(
              base64StringToBlob(pngFile, 'image/png')
            ),
            pngFile: base64StringToBlob(pngFile, 'image/png'),
            pngFileWithoutConverters: base64StringToBlob(pngFile, 'image/png'),
            pmsColorsInPngFile: [...artworkColors],
            height: recalculatedHeight,
            width: recalculatedWidth,
            aspectRatio: recalculatedWidth / recalculatedHeight,
            smallDetailsResult,
            pngWidthPixels: width,
            pngHeightPixels: height,
          });
        }
      });

      const mirrorStore = useMirrorStore();
      mirrorStore.updateMirrorShape(this.shapes[index]);
    },
    async applyEffectIfNeeded(
      positionId: string,
      techniqueId: string,
      shapePayload: AnnotationShape
    ) {
      const productStore = useProductStore();
      const isLaserTechnique = productStore.isLaserConverter(positionId);

      if (isLaserTechnique) {
        await this.applyLaserConverter(
          shapePayload.id,
          positionId,
          techniqueId
        );
      }

      if (productStore.isDebossingTechnique(positionId)) {
        await this.applyDebossingConverter(shapePayload.id);
      } else if (productStore.isPantoneColorTechnique()) {
        await this.applyPantoneConverter(shapePayload.id);
      }
    },

    async changeLogoBackground(proccessingType: string) {
      const actionsStore = useActionsStore();
      const command: SetArtworkBackgroundCommand = {
        isFullBackground: proccessingType === 'fullBackground',
        color: 2,
      };

      try {
        this.isLoaderActive = true;

        const activeShape = this.shapes.find(
          (x) => x.id === this.activeShapeId
        );
        if (activeShape && activeShape.kind === 'Logo') {
          const { data } = await FileService.changeLogoBackground(
            activeShape.pngFile,
            command
          );
          const whiteColor: PmsColor = {
            red: 255,
            green: 255,
            blue: 255,
            colorFuzz: 20,
            originalName: '',
            isOriginalSpotColor: false,
            name: 'Pure White',
            originalBlack: 0,
            originalCyan: 0,
            originalMagenta: 0,
            originalYellow: 0,
          };

          const artworkColors = activeShape.pmsColorsInPngFile;

          if (proccessingType === 'fullBackground') {
            actionsStore.createRemoveBackgroundFullAction(
              this.activeShapeId,
              whiteColor
            );
          }

          if (proccessingType !== 'fullBackground') {
            actionsStore.createRemoveBackgroundOutsideImageAction(
              this.activeShapeId,
              whiteColor
            );
          }

          if (data.isAllWhiteRemoved) {
            const whiteColorIndex = artworkColors.findIndex(
              (c) => c.name === whiteColor.name
            );
            if (whiteColorIndex !== -1) {
              artworkColors.splice(whiteColorIndex, 1);
            }
          }

          if (artworkColors.length !== 0) {
            this.updateArtworkBackground(
              data.pngFile,
              artworkColors,
              data.heightInPx,
              data.widthInPx
            );
          } else {
            await this.removeArtworkPayload(activeShape.id, false);
          }

          const notificationSore = useNotificationStore();
          notificationSore.revalidateColorsOnly();

          const historyStore = useHistoryStore();
          const snapshot = createStateSnapshot();
          historyStore.recordHistory(snapshot);
        }

        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async textToImage(
      font: string,
      fontSize: number,
      fontWeight: string,
      fontStyle: string,
      text: string,
      textAlignment: string,
      textColor: string,
      textHtml: string,
      color: [number, number, number, number?],
      repositionText: boolean,
      shapeId: string
    ) {
      try {
        this.isLoaderActive = true;

        const productStore = useProductStore();

        const positionId = productStore.selectedPrintPosition.id;
        const printPosition = productStore.data.productPrintPositions.find(
          (x) => x.id === positionId
        );
        const techniqueId = productStore.unavailablePrintPositions.find(
          (x) => x.positionId === positionId
        )?.technique.id;
        const printTechniqueGroupCode =
          printPosition?.printTechniques.find((x) => x.id === techniqueId)
            ?.printTechnique.group.code ?? '';

        const command: ProcessTextCommand = {
          font: font,
          fontSize: fontSize,
          id: shapeId,
          isBold: fontWeight === 'bold',
          isItalic: fontStyle === 'italic',
          text: text,
          textAlignment: textAlignment as TextAlignment,
          textColor: textColor,
          productTechniqueGroupCode: printTechniqueGroupCode,
        };

        const { data } = await FileService.textToImage(command);

        const textShapeIndex = _findIndex(this.shapes, {
          id: shapeId,
        });

        this.$patch((state) => {
          const current = state.shapes[textShapeIndex];

          if (current.kind === 'Text') {
            state.shapes.splice(textShapeIndex, 1, {
              ...current,
              backgroundImage: URL.createObjectURL(
                base64StringToBlob(data.pngFile, 'image/png')
              ),
              pngFile: base64StringToBlob(data.pngFile, 'image/png'),
              pngFileWithoutConverters: base64StringToBlob(
                data.pngFile,
                'image/png'
              ),
              height: data.pngHeightPixels,
              width: data.pngWidthPixels,
              originalWidthMM: data.originalWidthMM,
              originalHeightMM: data.originalHeightMM,
              smallDetailsResult:
                current.smallDetailsResult !== undefined
                  ? {
                    ...current.smallDetailsResult,
                    isCheckValid: false,
                  }
                  : undefined,
              w2pText: {
                ...current.w2pText,
                fontFamily: font,
                fontSizePT: fontSize,
                fontWeight,
                fontStyle,
                textAlign: textAlignment,
                text: text,
                textHtml: textHtml,
                colorName: textColor,
                color: color,
              },
            });
          }
        });

        positionText(
          this.shapes[textShapeIndex] as TextShape,
          productStore.selectedPrintPosition.id,
          repositionText
        );

        if (productStore.selectedPrintPosition.techniqueId) {
          await this.applyEffectIfNeeded(
            productStore.selectedPrintPosition.id,
            productStore.selectedPrintPosition.techniqueId,
            this.shapes[textShapeIndex]
          );
        }

        const mirrorStore = useMirrorStore();
        mirrorStore.updateMirrorShape(this.shapes[textShapeIndex]);

        const notificationStore = useNotificationStore();
        notificationStore.revalidateColorsOnly();

        const historyStore = useHistoryStore();
        const snapshot = createStateSnapshot();
        historyStore.recordHistory(snapshot);

        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async replaceColor(from: PmsColor, to: PmsColor) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();
      const mirrorStore = useMirrorStore();

      const textPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(textShapes)
        .filter((x) => x.kind === 'Text' && x.w2pText.colorName === from.name);

      textPrintPositionShapes.map((t) => {
        const textShapeIndex = _findIndex(this.shapes, {
          id: t.id,
        });

        const { red: r, green: g, blue: b } = to;

        this.$patch((state) => {
          const current = state.shapes[textShapeIndex];
          if (current.kind === 'Text') {
            this.textToImage(
              current.w2pText.fontFamily,
              current.w2pText.fontSizePT,
              current.w2pText.fontWeight,
              current.w2pText.fontStyle,
              current.w2pText.text,
              current.w2pText.textAlign,
              to.name,
              current.w2pText.textHtml,
              [to.red, to.green, to.blue, to.colorFuzz],
              false,
              current.id
            );
          }
        });

        mirrorStore.updateMirrorShape(this.shapes[textShapeIndex]);
      });

      const nonTextPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(nonTextShapes) as LogoShape[];

      const artworksForProcessing = nonTextPrintPositionShapes.filter((a) => {
        return a.pmsColorsInPngFile.some((c) => c.name.includes(from.name));
      });

      if (artworksForProcessing.length > 0) {
        const dataToReturn = [] as {
          id: string;
          file: string;
        }[];

        for (const artwork of artworksForProcessing) {
          const channel = artwork.pmsColorsInPngFile;
          const colorsToChange =
            artwork.pmsColorsInPngFile.filter((c) => c.name === from.name) ||
            from;
          let lastFile = '';
          let lastBlob = artwork.pngFileWithoutConverters as Blob;
          const mappedPngFile = {
            id: artwork.personalizationArtworkId,
            file: artwork.pngFile as Blob,
          };
          for (let i = 0; i < colorsToChange.length; i++) {
            const command: ChangeColorCommand = {
              oldColorToReplace: colorsToChange[i],
              newColorToSet: to,
              channels: channel,
            };

            try {
              this.isLoaderActive = true;

              const { data } = await FileService.replaceColor(command, [
                lastBlob,
              ]);

              actionsStore.createReplaceColorAction(mappedPngFile.id, from, to);

              lastFile = data.pngFiles[0];
              lastBlob = base64StringToBlob(data.pngFiles[0], 'image/png');
            } catch (error) {
              console.log('An error occurred', error);
              this.isLoaderActive = false;
            }
          }
          dataToReturn.push({
            id: mappedPngFile.id,
            file: lastFile,
          });
        }
        this.isLoaderActive = false;
        return dataToReturn;
      }
    },

    async makeLogoRound(currentShapeIndex: number) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();

      const command: DoRoundCroppingCommand = {
        croppingAreaAspectRatioX: '1',
        croppingAreaAspectRatioY:
          (productStore.activePosition.printArea?.heightMm as number) /
          (productStore.activePosition.printArea?.widthMm as number),
      };
      try {
        this.isLoaderActive = true;
        const current = this.shapes[currentShapeIndex];

        if (
          current.kind === 'Logo' &&
          productStore.selectedPrintPosition.techniqueId
        ) {
          const { data } = await FileService.roundCropping(
            current.pngFileWithoutConverters as Blob,
            command
          );

          const affectedShape = this.shapes[currentShapeIndex];

          const smallDetailsResult = affectedShape.smallDetailsResult
            ? {
              ...affectedShape.smallDetailsResult,
              isCheckValid: false,
            }
            : undefined;

          //Calculate position and size that will fill area:
          const {
            originalImageWebDpi,
            originalImageWidthPx,
            originalImageDpi,
          } = productStore.activePosition;
          const webImageWidthPx =
            originalImageWidthPx &&
            originalImageWebDpi &&
            originalImageDpi &&
            (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
          const rectLineWidth = (lineWidth * (webImageWidthPx as number)) / 100;
          const areaRect = {
            x:
              productStore.drawPoints(productStore.activePosition.id).x -
              productStore.drawPoints(productStore.activePosition.id).rx +
              rectLineWidth / 2,
            y:
              productStore.drawPoints(productStore.activePosition.id).y -
              productStore.drawPoints(productStore.activePosition.id).ry +
              rectLineWidth / 2,
            w:
              productStore.drawPoints(productStore.activePosition.id).rx * 2 -
              rectLineWidth,
            h:
              productStore.drawPoints(productStore.activePosition.id).ry * 2 -
              rectLineWidth,
          };

          let calcArtworkWidth = 0;
          let calcArtworkHeight = 0;
          const CORRECTION_FACTOR = 0;
          const artworkRatio = data.newWidth / data.newHeight;
          const areaRatio = areaRect.w / areaRect.h;
          // Artwork is more landscape than area - fit to width
          if (artworkRatio > areaRatio) {
            calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
            calcArtworkHeight =
              (calcArtworkWidth / data.newWidth) * data.newHeight;
          }
          // Artwork is more portrait than area - fit to height
          else {
            calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
            calcArtworkWidth =
              (calcArtworkHeight / data.newHeight) * data.newWidth;
          }

          //Calculate percentage croppend from each edge
          actionsStore.createCropAction(this.activeShapeId, {
            left:
              data.newWidth == current.pngWidthPixels
                ? 0
                : ((current.pngWidthPixels - data.newWidth / 2) /
                  current.pngWidthPixels) *
                100,
            right:
              data.newWidth == current.pngWidthPixels
                ? 0
                : ((current.pngWidthPixels - data.newWidth / 2) /
                  current.pngWidthPixels) *
                100,
            top:
              data.newHeight == current.pngHeightPixels
                ? 0
                : ((current.pngHeightPixels - data.newHeight / 2) /
                  current.pngHeightPixels) *
                100,
            bottom:
              data.newHeight == current.pngHeightPixels
                ? 0
                : ((current.pngHeightPixels - data.newHeight / 2) /
                  current.pngHeightPixels) *
                100,
          });

          this.$patch((state) => {
            state.shapes.splice(currentShapeIndex, 1, {
              ...current,
              backgroundImage: URL.createObjectURL(
                base64StringToBlob(data.pngFile, 'image/png')
              ),
              pngFile: base64StringToBlob(data.pngFile, 'image/png'),
              pngFileWithoutConverters: base64StringToBlob(
                data.pngFile,
                'image/png'
              ),
              pngHeightPixels: data.newHeight,
              pngWidthPixels: data.newWidth,
              width: calcArtworkWidth,
              height: calcArtworkHeight,
              aspectRatio: calcArtworkWidth / calcArtworkHeight,
              smallDetailsResult,
              x: areaRect.x + lineWidth,
              y: areaRect.y + lineWidth,
              isRoundLogo: true,
            });
          });

          await this.applyEffectIfNeeded(
            productStore.selectedPrintPosition.id,
            productStore.selectedPrintPosition.techniqueId,
            current
          );

          const historyStore = useHistoryStore();
          const snapshot = createStateSnapshot();
          historyStore.recordHistory(snapshot);
        }

        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },

    async removeColor(color: PmsColor) {
      const productStore = useProductStore();
      const actionsStore = useActionsStore();
      const colorMngStore = useColorMngStore();

      const textPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(textShapes)
        .filter((t) => t.kind === 'Text' && t.w2pText.colorName === color.name);

      textPrintPositionShapes.map((t) => {
        const textShapeIndex = _findIndex(this.shapes, {
          id: t.id,
        });

        if (textShapeIndex !== -1) {
          this.removeTextPayload(t.id, false);
        }
      });

      const nonTextPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(nonTextShapes) as LogoShape[];

      const artworksForProcessing = nonTextPrintPositionShapes.filter((a) => {
        return a.pmsColorsInPngFile.some((c) => c.name.includes(color.name));
      });

      if (artworksForProcessing.length > 0) {
        const colorsWithSameName = new Array<PmsColor>();
        artworksForProcessing.forEach((artwork) => {
          artwork.pmsColorsInPngFile.forEach((pmsColor) => {
            if (pmsColor.name == color.name) {
              colorsWithSameName.push(pmsColor);
            }
          });
        });
        const mapedPngFiles = artworksForProcessing.map((a) => {
          return {
            id: a.personalizationArtworkId,
            file: a.pngFileWithoutConverters as Blob,
          };
        });
        let latestBlobs = mapedPngFiles.map((x) => {
          return x.file;
        });
        let latestData: RemoveColorResult[];
        for (let i = 0; i < colorsWithSameName.length; i++) {
          const command: RemoveColorCommand = {
            colorToRemove: colorsWithSameName[i],
            channels: colorMngStore.getPmsColorsOnPrintPosition,
          };
          try {
            this.isLoaderActive = true;

            const { data } = await FileService.removeColor(
              command,
              latestBlobs
            );
            latestBlobs = data.map((x) => {
              return base64StringToBlob(x.pngFile);
            });
            latestData = data;
          } catch (error) {
            this.isLoaderActive = false;
            console.log('An error occurred', error);
          }
        }
        const returnData = mapedPngFiles.map((x, i) => {
          actionsStore.createRemoveColorAction(x.id, color);
          return { id: x.id, file: latestData[i] };
        });

        this.isLoaderActive = false;
        return returnData;
      }
    },
    async revertArtworkBackground() {
      const whiteColor: PmsColor = {
        red: 255,
        green: 255,
        blue: 255,
        colorFuzz: 20,
        originalName: '',
        isOriginalSpotColor: false,
        name: 'Pure White',
        originalBlack: 0,
        originalCyan: 0,
        originalMagenta: 0,
        originalYellow: 0,
      };
      const command: SetArtworkBackgroundCommand = {
        isFullBackground: true,
        color: 1, //White
        doRevertBackground: true,
      };

      try {
        this.isLoaderActive = true;
        const activeShape = this.shapes.find(
          (x) => x.id === this.activeShapeId
        );

        if (activeShape && activeShape.kind === 'Logo') {
          const { data } = await FileService.changeLogoBackground(
            activeShape.pngFile,
            command
          );
          let artworkColors = [...activeShape.pmsColorsInPngFile];
          const whiteAlreadyExists =
            artworkColors.findIndex((c) => c.name === whiteColor.name) !== -1;
          if (!whiteAlreadyExists) {
            artworkColors = [...artworkColors, whiteColor];
          }

          this.updateArtworkBackground(
            data.pngFile,
            [...artworkColors],
            data.heightInPx,
            data.widthInPx
          );

          const notificationStore = useNotificationStore();
          notificationStore.revalidateColorsOnly();

          const historyStore = useHistoryStore();
          const snapshot = createStateSnapshot();
          historyStore.recordHistory(snapshot);
        }

        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    updateBackground(pngFile: string, shapePayload: AnnotationShape) {
      const shapeIndex = this.shapes.findIndex(
        (shape: AnnotationShape) => shape.id === shapePayload.id
      );
      const current = this.shapes[shapeIndex];
      if (current) {
        this.$patch((state) => {
          state.shapes.splice(shapeIndex, 1, {
            ...current,
            backgroundImage: pngFile,
          });
        });
      }
    },
    async applyLaserConverter(
      shapeId: string,
      positionId: string,
      techniqueId: string
    ) {
      try {
        this.isLoaderActive = true;
        const productStore = useProductStore();
        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find(
              (v) => el.variantColor == v.colour
            ),
          }));

        const shapePayload = {
          ...this.shapes[this.shapes.findIndex((x) => x.id === shapeId)],
        };

        if (shapePayload) {
          shapePayload.variantArtworkImages = [];

          await Promise.all(
            mappedVariants.map(async (v) => {
              const laserConfiguration: LaserConfiguration =
                productStore.getVariantLaserColor(
                  positionId,
                  techniqueId,
                  v.sku as string
                );
              const laserEffectsCommand = {
                laserEffects: [
                  {
                    variantColor: v.variantColor,
                    laserPercentage: laserConfiguration.laserPercentage,
                    laserColor: laserConfiguration.laserColor,
                  },
                ],
              };
              if (laserConfiguration && shapePayload) {
                const { data } = await FileService.applyLaserEffect(
                  shapePayload.pngFileWithoutConverters as Blob,
                  JSON.stringify(laserEffectsCommand)
                );

                const variantImages = shapePayload.variantArtworkImages;
                this.$patch((state) => {
                  state.shapes.splice(
                    this.shapes.findIndex((x) => x.id === shapeId),
                    1,
                    {
                      ...state.shapes[
                      this.shapes.findIndex((x) => x.id === shapeId)
                      ],
                      ...{
                        variantArtworkImages: [
                          ...shapePayload.variantArtworkImages,
                          {
                            variantColor: v.colour as string,
                            variantArtworkPng: base64StringToBlob(
                              data.results[0].pngFile,
                              'image/png'
                            ),
                            leftPercentage: 0,
                            topPercentage: 0,
                            widthPercentage: 0,
                            heightPercentage: 0,
                          },
                        ],
                      },

                      ...(productStore.selectedVariant.colour ===
                        v.variantColor && {
                        pngFile: base64StringToBlob(
                          data.results[0].pngFile,
                          'image/png'
                        ),
                      }),
                    }
                  );
                });
                shapePayload.variantArtworkImages = [
                  ...variantImages,
                  {
                    variantColor: v.colour as string,
                    variantArtworkPng: base64StringToBlob(
                      data.results[0].pngFile,
                      'image/png'
                    ),
                    leftPercentage: 0,
                    topPercentage: 0,
                    widthPercentage: 0,
                    heightPercentage: 0,
                  },
                ];
              }
            })
          );

          const actionsStore = useActionsStore();

          if (shapePayload && shapePayload.kind === 'Logo') {
            actionsStore.createLaserConverterAction(
              shapePayload.personalizationArtworkId
            );
          }

          const updatedShape =
            this.shapes[this.shapes.findIndex((x) => x.id === shapeId)];
          const image = updatedShape.variantArtworkImages.find(
            (i) => i.variantColor === productStore.selectedVariant.colour
          )?.variantArtworkPng;

          if (image) {
            this.updateBackground(
              URL.createObjectURL(image),
              this.shapes[this.shapes.findIndex((x) => x.id === shapeId)]
            );
          }
        }
        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async applyPantoneConverter(shapeId: string) {
      try {
        this.isLoaderActive = true;
        const productStore = useProductStore();
        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find(
              (v) => el.variantColor == v.colour
            ),
          }));

        const shapePayload = {
          ...this.shapes[this.shapes.findIndex((x) => x.id === shapeId)],
        };

        if (shapePayload && shapePayload.kind === 'Logo') {
          shapePayload.variantArtworkImages = [];
          const colorManagementStore = useColorsManagementStore();

          await Promise.all(
            mappedVariants.map(async (v) => {
              const changeColorsCommand = {
                replaceColors: shapePayload.pmsColorsInPngFile.map((x) => {
                  const newColorToSet =
                    colorManagementStore.availablePmsColors.find(
                      (y) => y.name === x.name
                    );
                  return {
                    oldColorToReplace: x,
                    newColorToSet: newColorToSet ? newColorToSet : x,
                  };
                }),
              };

              const { data } = await FileService.replaceColors(
                changeColorsCommand,
                shapePayload.pngFileWithoutConverters as Blob
              );

              const variantImages = shapePayload.variantArtworkImages;
              this.$patch((state) => {
                state.shapes.splice(
                  this.shapes.findIndex((x) => x.id === shapeId),
                  1,
                  {
                    ...state.shapes[
                    this.shapes.findIndex((x) => x.id === shapeId)
                    ],
                    ...{
                      variantArtworkImages: [
                        ...shapePayload.variantArtworkImages,
                        {
                          variantColor: v.colour as string,
                          variantArtworkPng: base64StringToBlob(
                            data,
                            'image/png'
                          ),
                          leftPercentage: 0,
                          topPercentage: 0,
                          widthPercentage: 0,
                          heightPercentage: 0,
                        },
                      ],
                    },

                    ...(productStore.selectedVariant.colour ===
                      v.variantColor && {
                      pngFile: base64StringToBlob(data, 'image/png'),
                    }),
                  }
                );
              });
              shapePayload.variantArtworkImages = [
                ...variantImages,
                {
                  variantColor: v.colour as string,
                  variantArtworkPng: base64StringToBlob(data, 'image/png'),
                  leftPercentage: 0,
                  topPercentage: 0,
                  widthPercentage: 0,
                  heightPercentage: 0,
                },
              ];
            })
          );

          const actionsStore = useActionsStore();

          if (shapePayload && shapePayload.kind === 'Logo') {
            actionsStore.createPantoneConverterAction(
              shapePayload.personalizationArtworkId
            );
          }

          const updatedShape =
            this.shapes[this.shapes.findIndex((x) => x.id === shapeId)];
          const image = updatedShape.variantArtworkImages.find(
            (i) => i.variantColor === productStore.selectedVariant.colour
          )?.variantArtworkPng;

          if (image) {
            this.updateBackground(
              URL.createObjectURL(image),
              this.shapes[this.shapes.findIndex((x) => x.id === shapeId)]
            );
          }
        }
        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async applyDebossingConverter(shapeId: string) {
      try {
        this.isLoaderActive = true;
        const productStore = useProductStore();
        const mappedVariants =
          productStore.selectedVariants &&
          productStore.selectedVariants.map((el) => ({
            ...el,
            ...productStore.data.variants.find(
              (v) => el.variantColor == v.colour
            ),
          }));

        const shapePayload = {
          ...this.shapes[this.shapes.findIndex((x) => x.id === shapeId)],
        };
        if (shapePayload) {
          shapePayload.variantArtworkImages = [];

          await Promise.all(
            mappedVariants.map(async (v) => {
              if (shapePayload) {
                const variantImage = v.images?.find(
                  (i) =>
                    i.printPositionID == productStore.selectedPrintPosition.id
                );
                if (variantImage && variantImage.mostUsedColor) {
                  const { data } = await FileService.applyDebossingEffect(
                    shapePayload.pngFileWithoutConverters as Blob,
                    JSON.stringify({
                      debossingEffects: [
                        {
                          colorHex: variantImage.mostUsedColor,
                          variantColor: v.variantColor,
                        },
                      ],
                    })
                  );

                  const variantImages = shapePayload.variantArtworkImages;
                  this.$patch((state) => {
                    state.shapes.splice(
                      this.shapes.findIndex((x) => x.id === shapeId),
                      1,
                      {
                        ...state.shapes[
                        this.shapes.findIndex((x) => x.id === shapeId)
                        ],
                        ...{
                          variantArtworkImages: [
                            ...shapePayload.variantArtworkImages,
                            {
                              variantColor: v.colour as string,
                              variantArtworkPng: base64StringToBlob(
                                data.results[0].pngFile,
                                'image/png'
                              ),
                              leftPercentage: 0,
                              topPercentage: 0,
                              widthPercentage: 0,
                              heightPercentage: 0,
                            },
                          ],
                        },

                        ...(productStore.selectedVariant.colour ===
                          v.variantColor && {
                          pngFile: base64StringToBlob(
                            data.results[0].pngFile,
                            'image/png'
                          ),
                        }),
                      }
                    );
                  });
                  shapePayload.variantArtworkImages = [
                    ...variantImages,
                    {
                      variantColor: v.colour as string,
                      variantArtworkPng: base64StringToBlob(
                        data.results[0].pngFile,
                        'image/png'
                      ),
                      leftPercentage: 0,
                      topPercentage: 0,
                      widthPercentage: 0,
                      heightPercentage: 0,
                    },
                  ];
                }
              }
            })
          );

          const actionsStore = useActionsStore();
          if (shapePayload) {
            if (shapePayload.kind === 'Logo') {
              actionsStore.createDebossingConverterAction(
                shapePayload.personalizationArtworkId
              );
            }

            const updatedShape =
              this.shapes[this.shapes.findIndex((x) => x.id === shapeId)];
            const image = updatedShape.variantArtworkImages.find(
              (i) => i.variantColor === productStore.selectedVariant.colour
            )?.variantArtworkPng;

            if (image) {
              this.updateBackground(
                URL.createObjectURL(image),
                this.shapes[this.shapes.findIndex((x) => x.id === shapeId)]
              );
            }
          }
        }
        this.isLoaderActive = false;
      } catch (error) {
        this.isLoaderActive = false;
        console.log('An error occurred', error);
      }
    },
    async convertToBlob(fileUrl: string) {
      const res = await axios({
        url: fileUrl,
        method: 'GET',
        responseType: 'blob',
      });
      return res.data;
    },

    async initializeStateFromPersonalization(personalization: Personalization) {
      const productStore = useProductStore();
      const actionStore = useActionsStore();

      const printPositions = personalization.proofs[0].productPrintPositions;

      for (const pos of printPositions) {
        const product = productStore.data.productPrintPositions.find(
          (p) => p.name === pos.name
        );
        const positionID = product?.id;
        const originalDPI = product?.originalImageWebDpi as number;

        let mappedArtworkIds: Record<string, string>[] =
          pos.personalizationArtworks.map((a) => {
            return {
              id: positionID,
              logoId: a.id,
            } as Record<string, string>;
          });

        const isLaserConverter = productStore.isLaserConverter(
          positionID,
          pos.productPrintTechnique.sapCode
        );
        const isDebossing = productStore.isDebossingTechnique(
          positionID,
          pos.productPrintTechnique.sapCode
        );

        this.positionShapeMap = [...this.positionShapeMap, ...mappedArtworkIds];
        mappedArtworkIds = [];

        for (const a of pos.personalizationArtworks) {
          const imageBlob = await this.convertToBlob(a.pngFileUrl);

          let variantArtworkImages: VariantArtworkImages[] = [];

          if (isLaserConverter || isDebossing) {
            variantArtworkImages = await Promise.all(
              a.variantArtworkImages.map(async (vi) => {
                const imageBlob = await this.convertToBlob(
                  vi.variantArtworkPngUrl
                );
                return {
                  variantColor: vi.variantColor,
                  variantArtworkPng: imageBlob,
                } as VariantArtworkImages;
              })
            );
          }

          let smallDetailsPayload: SmallDetailsPayload | undefined = undefined;

          if (DoSmallDetailsPart) {
            const isSmallDetailsTechnique =
              productStore.isSmallDetailsTechnique(
                positionID,
                pos.productPrintTechnique.sapCode
              );
            if (a.smallDetails) {
              smallDetailsPayload = {
                negativeDetailsLoss: a.smallDetails.negativeLoss,
                positiveDetailsLoss: a.smallDetails.positiveLoss,
                status: a.smallDetails.status,
                approvedAt: a.smallDetails.approvedAt
                  ? a.smallDetails.approvedAt.toString()
                  : undefined,
                isCheckValid: true,
                pngFile: a.smallDetails.fileUrl,
                pngFileBlob: await this.convertToBlob(a.smallDetails.fileUrl),
                calculatedQuality: a.smallDetails.calculatedQuality,
                id: a.id,
              };
            }

            if (isSmallDetailsTechnique && a.smallDetails === null) {
              smallDetailsPayload = {
                negativeDetailsLoss: 0,
                positiveDetailsLoss: 0,
                status: 'NoCheck',
                approvedAt: undefined,
                isCheckValid: true,
                pngFile: '',
                pngFileBlob: new Blob(),
                calculatedQuality: 0,
                id: a.id,
              };
            }
          }

          this.shapes.push({
            zindex: a.zIndex,
            isRoundLogo: a.isRoundLogo,
            id: a.id,
            pngFile: imageBlob,
            pngFileWithoutConverters: imageBlob,
            title: a.title !== null ? a.title : '',
            originalFileUrl: a.originalFileUrl,
            originalHorizontalResolutionDPIs:
              a.originalHorizontalResolutionDPIs,
            originalVerticalResolutionDPIs: a.originalVerticalResolutionDPIs,
            pngHorizontalResolutionDPIs: a.pngHorizontalResolutionDPIs,
            pngVerticalResolutionDPIs: a.pngVerticalResolutionDPIs,
            pngHeightPixels: a.pngHeightPixels,
            pngWidthPixels: a.pngWidthPixels,
            pmsColorsInPngFile: a.pmsColorsInPngFile as PmsColor[],
            personalizationArtworkId: a.id,
            customerId: '',
            fileName: a.fileName,
            originalHeightMM: a.originalHeightMM,
            originalWidthMM: a.originalWidthMM,
            thumbnailUrl: '',
            pngFileUrl: a.pngFileUrl,
            isVectorFileTrueVectorFile: a.isVectorFileTrueVectorFile,
            spotColorsInOriginalFile: [],
            customerArtworkId: '',
            processed: '',
            x: mm2px(a.distanceFromLeftWithCropping, originalDPI),
            y: mm2px(a.distanceFromTopWithCropping, originalDPI),
            width: mm2px(a.widthWithCropping, originalDPI),
            height: mm2px(a.heightWithCropping, originalDPI),
            rotation: a.rotationAngle * (Math.PI / 180),
            aspectRatio:
              mm2px(a.widthWithCropping, originalDPI) /
              mm2px(a.heightWithCropping, originalDPI),
            backgroundImage: URL.createObjectURL(
              variantArtworkImages.find(
                (v) => v.variantColor === productStore.selectedVariant.colour
              )?.variantArtworkPng || imageBlob
            ),
            smallDetailsResult: smallDetailsPayload,
            isInArea: false,
            isSelected: false,
            alignedTo: '',
            variantArtworkImages: variantArtworkImages,
            kind: 'Logo',
          } as AnnotationShape);

          actionStore.initializeUserActionForArtwork(a.id, a.userActions);
        }

        for (const t of pos.personalizationTexts) {
          const imageBlob =
            t.pngFileUrl !== null ? await this.convertToBlob(t.pngFileUrl) : '';

          let colorRGBArray = [];

          let smallDetailsPayload: SmallDetailsPayload | undefined = undefined;

          if (DoSmallDetailsPart && t.smallDetails) {
            smallDetailsPayload = {
              negativeDetailsLoss: t.smallDetails.negativeLoss,
              positiveDetailsLoss: t.smallDetails.positiveLoss,
              status: t.smallDetails.status,
              approvedAt: t.smallDetails.approvedAt
                ? t.smallDetails.approvedAt.toString()
                : undefined,
              isCheckValid: true,
              pngFile: t.smallDetails.fileUrl,
              pngFileBlob: await this.convertToBlob(t.smallDetails.fileUrl),
              calculatedQuality: t.smallDetails.calculatedQuality,
              id: t.id,
            };
          }

          if (isLaserConverter) {
            const techniqueId = product?.printTechniques.find(
              (x) => x.printTechnique.code === pos.productPrintTechnique.sapCode
            )?.id;
            const laserColor = productStore.getVariantLaserColor(
              product?.id as string,
              techniqueId as string,
              productStore.selectedVariant.sku
            );
            const { red: r, green: g, blue: b } = laserColor.laserColor;
            colorRGBArray = [r, g, b];
          } else {
            const { red: r, green: g, blue: b } = t.pmsColorInfo;
            colorRGBArray = [r, g, b];
          }

          let variantArtworkImages: VariantArtworkImages[] = [];
          if (isLaserConverter || isDebossing) {
            variantArtworkImages = await Promise.all(
              t.variantArtworkImages.map(async (vi) => {
                const imageBlob = await this.convertToBlob(
                  vi.variantArtworkPngUrl
                );
                return {
                  variantColor: vi.variantColor,
                  variantArtworkPng: imageBlob,
                } as VariantArtworkImages;
              })
            );
          }

          this.positionShapeMap = [
            ...this.positionShapeMap,
            { id: positionID, logoId: t.id } as Record<string, string>,
          ];

          const newShape = {
            zindex: t.zIndex,
            isRoundLogo: false,
            id: t.id,
            width: t.width === 0 ? 1500 : mm2px(t.width, originalDPI), // this will be deleted when Dario fix issue on back side
            height: t.height === 0 ? 500 : mm2px(t.height, originalDPI), // this will be deleted when Dario fix issue on back side
            x: mm2px(t.distanceFromLeft, originalDPI),
            y: mm2px(t.distanceFromTop, originalDPI),
            rotation: t.rotationAngle * (Math.PI / 180),
            title: t.text,
            disableInput: true,
            smallDetailsResult: smallDetailsPayload,
            isInArea: false,
            isSelected: false,
            kind: 'Text',
            backgroundImage:
              t.pngFileUrl !== null
                ? URL.createObjectURL(
                  variantArtworkImages.find(
                    (v) =>
                      v.variantColor === productStore.selectedVariant.colour
                  )?.variantArtworkPng || imageBlob
                )
                : null,
            pngFile: imageBlob,
            pngFileWithoutConverters: imageBlob,
            variantArtworkImages: variantArtworkImages,
            originalWidthMM: t.originalWidthMM,
            originalHeightMM: t.originalHeightMM,
            disableResize: true,
            w2pText: {
              text: t.text,
              textHtml: t.textHtml,
              fontSizePT: t.fontSize,
              fontSize: t.fontSize * (originalDPI / 72),
              fontWeight: t.bold ? 'bold' : 'normal',
              fontStyle: t.italic ? 'italic' : 'normal',
              fontFamily: t.font,
              textAlign: t.textAlignement.toLowerCase(),
              colorName: t.pmsColorInfo.name,
              color: colorRGBArray,
              fontVariant: '',
              alignedTo: '',
            } as Text,
          } as TextShape;

          // generate the image for old personalizationtext
          this.shapes.push(newShape as AnnotationShape);
          if (newShape.backgroundImage === null) {
            this.textToImage(
              newShape.w2pText.fontFamily,
              newShape.w2pText.fontSizePT,
              newShape.w2pText.fontWeight,
              newShape.w2pText.fontStyle,
              newShape.w2pText.text,
              newShape.w2pText.textAlign,
              newShape.w2pText.colorName,
              newShape.w2pText.textHtml,
              newShape.w2pText.color,
              true,
              newShape.id
            );
          }
        }

        this.shapes.sort((a, b) => a.zindex - b.zindex);
      }
    },

    async initializeStateFromPersonalizationJson(
      personalizationJson: CreatePersonalizationFormWrapper
    ) {
      const productStore = useProductStore();
      const actionStore = useActionsStore();

      const printPositions = personalizationJson.form.newProofForm
        .newProductPrintPositionForms as any[];

      for (const pos of printPositions) {
        const product = productStore.data.productPrintPositions.find(
          (p) => p.name === pos.name
        );
        const positionID = product?.id || '';
        const originalDPI = product?.originalImageWebDpi as number;

        const technique = product?.printTechniques.find(
          (x) => x.printTechnique.code === pos.productPrintTechnique.SAPCode
        );

        let mappedArtworkIds: Record<string, string>[] =
          pos.personalizationArtworks.map((a: any) => {
            return {
              id: positionID,
              logoId: a.id,
            } as Record<string, string>;
          });

        this.positionShapeMap = [...this.positionShapeMap, ...mappedArtworkIds];
        mappedArtworkIds = [];

        for (const a of pos.personalizationArtworks) {
          const imageBlob = base64StringToBlob(
            personalizationJson.files.find((file) => file.name === a.id)
              ?.file || '',
            'image/png'
          );

          const isSmallDetailsTechnique = productStore.isSmallDetailsTechnique(
            positionID,
            pos.productPrintTechnique.SAPCode
          );

          if (imageBlob) {
            const shapeToAdd = {
              zindex: 1,
              isRoundLogo: false,
              id: a.id,
              pngFile: imageBlob,
              pngFileWithoutConverters: imageBlob,
              title: a.title !== null ? a.title : '',
              originalFileUrl: a.originalFileUrl,
              originalHorizontalResolutionDPIs:
                a.originalHorizontalResolutionDPIs,
              originalVerticalResolutionDPIs: a.originalVerticalResolutionDPIs,
              pngHorizontalResolutionDPIs: a.pngHorizontalResolutionDPIs,
              pngVerticalResolutionDPIs: a.pngVerticalResolutionDPIs,
              pngHeightPixels: a.pngHeightPixels,
              pngWidthPixels: a.pngWidthPixels,
              pmsColorsInPngFile: a.pmsColorsInPngFile as PmsColor[],
              personalizationArtworkId: a.id,
              customerId: '',
              fileName: a.fileName,
              originalHeightMM: a.originalHeightMM,
              originalWidthMM: a.originalWidthMM,
              thumbnailUrl: '',
              pngFileUrl: '',
              isVectorFileTrueVectorFile: a.isVectorFileTrueVectorFile,
              spotColorsInOriginalFile: [],
              customerArtworkId: '',
              processed: '',
              x: mm2px(a.distanceFromleftWithCropping, originalDPI),
              y: mm2px(a.distanceFromTopWithCropping, originalDPI),
              width: mm2px(a.widthWithCropping, originalDPI),
              height: mm2px(a.heightWithCropping, originalDPI),
              rotation: a.rotationAngle ? a.rotationAngle * (Math.PI / 180) : 0,
              aspectRatio:
                mm2px(a.widthWithCropping, originalDPI) /
                mm2px(a.heightWithCropping, originalDPI),
              backgroundImage: URL.createObjectURL(imageBlob),
              isInArea: false,
              isSelected: false,
              alignedTo: '',
              variantArtworkImages: [],
              kind: 'Logo',
              smallDetailsResult: isSmallDetailsTechnique
                ? DefaultSmallDetailsResult
                : undefined,
            } as AnnotationShape;

            this.shapes.push(shapeToAdd);

            if (technique) {
              await this.applyEffectIfNeeded(
                positionID,
                technique.id,
                shapeToAdd
              );
            }
          }

          actionStore.initializeUserActionForArtwork(a.id, a.userActions);
        }

        this.shapes.sort((a, b) => a.zindex - b.zindex);
      }
    },
  },
});

export const attachActionSubscriber = (): (() => void) =>
  useStore().$onAction(({ name, store, args, after, onError }) => {
    after(async (result: any) => {
      console.log(
        `%c [action "${name}" "${args.join(', ')}"]:  `,
        'color: #3a539b'
      );
      const productStore = useProductStore();
      const notificationStore = useNotificationStore();
      const mirrorStore = useMirrorStore();
      const historyStore = useHistoryStore();

      /**
       * Keep track of added artworks, along with their respective position
       */
      if (name === ADD_ARTWORK_PAYLOAD || name === ADD_TEXT_PAYLOAD) {
        const shapePayload = args[0] as AnnotationShape;
        const positionId = args[1] as string;

        store.positionShapeMap.push({
          id: positionId,
          logoId: shapePayload.id,
        });
      }

      if (name === ADD_ARTWORK_PAYLOAD) {
        const shapePayload = args[0] as AnnotationShape;
        const positionId = args[1] as string;
        const techniqueId = args[2] as string;

        if (shapePayload.kind === 'Logo') {
          store.applyEffectIfNeeded(positionId, techniqueId, shapePayload);
        }
      }

      if (name === ADD_ARTWORK_PAYLOAD || name === ADD_TEXT_PAYLOAD) {
        const shapePayload = args[0] as AnnotationShape;
        const positionId = args[1] as string;
        const techniqueId = args[2] as string;

        const relevantShapeMap = store.positionShapeMap.filter(
          (p) => p.id === positionId
        );
        const relevantShapes = store.shapes.filter((s) => {
          return relevantShapeMap.find((sm) => sm.logoId === s.id);
        }) as AnnotationShape[];

        const activePosition = productStore.printPositions?.find((p) => {
          return p.id === positionId;
        });

        let currentPrintPosition = {} as ProductPrintPositionBoxModel;

        if (activePosition) {
          const techniqueName = activePosition.printTechniques.find(
            (technique) => technique.id === techniqueId
          )?.printTechnique.name;

          currentPrintPosition = {
            id: activePosition.id,
            name: activePosition.name,
            originalImageUrl: activePosition.originalImageUrl,
            originalImageDpi: activePosition.originalImageDpi,
            originalImageWebUrl: activePosition.originalImageWebUrl,
            originalImageWebDpi: activePosition.originalImageWebDpi,
            techniqueName: techniqueName,
            techniqueId: techniqueId,
            maximumColours: activePosition.printTechniques.find((technique) => {
              return technique.id === techniqueId;
            })?.maximumColours,
            maxWidthMm: activePosition.maxWidthMm,
            maxHeightMm: activePosition.maxHeightMm,
          };
        }

        if (name === ADD_ARTWORK_PAYLOAD) {
          const pipeline = constructArtworkValidationPipeline(
            productStore.data,
            [currentPrintPosition],
            relevantShapes,
            shapePayload
          );
          // Invoking the validation pipeline
          const validatorPipelineResults = pipeline({
            payload: shapePayload,
            validationResult: [],
          });
          notificationStore.allValidatorPipelineResults.push(
            validatorPipelineResults
          );
        }
        notificationStore.revalidateColorsOnly();
      }

      // CHECK SMALL DETAILS RESULTS
      if (DoSmallDetailsPart && name === ADD_ARTWORK_PAYLOAD) {
        const shapePayload = args[0] as AnnotationShape;
        const positionId = args[1] as string;

        if (
          shapePayload.smallDetailsResult &&
          shapePayload.smallDetailsResult.status !== 'NoCheck'
        ) {
          const shapeValidationIdx =
            notificationStore.allValidatorPipelineResults.findIndex(
              (x) => x.payload.id === shapePayload.id
            );

          const smallDetailsValidator = {
            printPositionId: positionId,
            validatorName: 'smallDetails',
            validatorColor: getSmallDetailsValidatorColor(
              shapePayload.smallDetailsResult.status
            ),
          };

          if (shapeValidationIdx !== -1) {
            notificationStore.allValidatorPipelineResults
              .find((x) => x.payload.id === shapePayload.id)
              ?.validationResult.push(smallDetailsValidator);
          } else {
            notificationStore.allValidatorPipelineResults.push({
              payload: shapePayload,
              validationResult: [smallDetailsValidator],
            });
          }
          notificationStore.setNotificationsForBell();
        }
      }

      if (
        mirrorStore.currentMirrorSettings &&
        mirrorStore.currentMirrorSettings.isMirrorApplied &&
        (name === ADD_ARTWORK_PAYLOAD || name === ADD_TEXT_PAYLOAD)
      ) {
        const shapePayload = args[0] as AnnotationShape;
        mirrorStore.addMirrorShape(shapePayload);
      }

      if (name === ADD_ARTWORK_PAYLOAD) {
        const createSnapshot = args[3];
        if (createSnapshot) {
          const snapshot = createStateSnapshot();
          historyStore.recordHistory(snapshot);
        }
      }

      if (name === REMOVE_ARTWORK_PAYLOAD) {
        const removedId = args[0];
        const stateAfterRemoval = store.positionShapeMap.filter((psm) => {
          return psm.logoId !== removedId;
        });
        store.positionShapeMap = stateAfterRemoval;
      }

      if (name === REMOVE_ARTWORK_PAYLOAD) {
        const removedId = args[0];
        const stateAfterRemoval = store.shapes.filter((s) => {
          return s.id !== removedId;
        });
        store.shapes.splice(0, store.shapes.length);
        stateAfterRemoval.forEach((x) => {
          store.shapes.push(x);
        });
      }

      if (name === REMOVE_ARTWORK_PAYLOAD) {
        // would be nice to revalidate all instead of just deleting
        const removedId = args[0];
        const stateAfterRemoval = notificationStore.notificationsForBell.filter(
          (r) => {
            return r.payloadId !== removedId;
          }
        );
        notificationStore.notificationsForBell = stateAfterRemoval;
        notificationStore.revalidateEmptyPrintPositions();
      }

      if (name === REMOVE_ARTWORK_PAYLOAD) {
        // would be nice to revalidate all instead of just deleting
        const removedId = args[0];
        const stateAfterRemoval =
          notificationStore.allValidatorPipelineResults.filter((r) => {
            return r.payload.id !== removedId;
          });
        notificationStore.allValidatorPipelineResults = stateAfterRemoval;
      }

      if (
        mirrorStore.currentMirrorSettings &&
        mirrorStore.currentMirrorSettings.isMirrorApplied &&
        (name === REMOVE_ARTWORK_PAYLOAD || name === REMOVE_TEXT_PAYLOAD)
      ) {
        const removedId = args[0] as string;
        mirrorStore.removeMirrorShape(removedId);
      }

      if (name === REMOVE_ARTWORK_PAYLOAD) {
        // would be nice to revalidate all instead of just deleting
        const createSnapshot = args[1];
        if (createSnapshot) {
          const snapshot = createStateSnapshot();
          historyStore.recordHistory(snapshot);
        }
      }
    });

    // this will trigger if the action throws or returns a promise that rejects
    onError((error) => {
      console.warn(`[ error ]: "${name}".\nError: ${error}.`);
    });
  });
